/**
 * @wl/widget-mortgage — функция рендера HTML.
 *
 * Возвращает HTML string для shadow root. Структура 1-в-1 как в inline-v2.html.
 * Динамические части (платежи, описания) заполняются при первом recalc(),
 * здесь — только initial markup с placeholder-значениями из defaults.
 */

import { getBankLogoSvg } from '@wl/bank-catalog';
import { normalizeChildren } from '@wl/shared-ui/family-mortgage';
import type { WidgetConfig } from './types';
import { formatNum, formatPct } from './format';
import { CHILDREN_LABELS, NO_OFFERS_TITLE, OTHER_PROGRAM_LABEL } from './family-text';

/** Дефолтные диапазоны слайдеров — как в прототипе v2. */
export const DEFAULT_RANGES = {
  price: { min: 1_000_000, max: 20_000_000, step: 100_000, default: 4_500_000 },
  // 20,1 % — порог, при котором доступны все банки каталога (у Альфы, Сбера и
  // ВТБ минимальный взнос 20.10 %). Шаг 0,1 обязателен: при шаге 1 браузер
  // округлил бы стартовое значение до 20 и половина банков отвалилась бы.
  downPaymentPct: { min: 20.1, max: 90, step: 0.1, default: 20.1 },
  term: { min: 5, max: 30, step: 1, default: 30 },
} as const;

/**
 * @param tenantSlug — слаг из атрибута `tenant`, а НЕ config.tenantId: сервер
 * присылает в конфиге UUID, и в отчётах Метрики кампания выглядела бы как
 * 9fc9650f-f422-… вместо имени клиента. Поймано на живом бейдже 04.08.2026.
 */
export function renderTemplate(config: WidgetConfig, tenantSlug = ''): string {
  // Плановые флаги (server-driven, ADR-0001). Поле может отсутствовать → дефолт «включено».
  const leadForm = config.leadForm !== false;
  const branding: 'full' | 'compact' | 'none' = config.branding ?? 'full';
  // Доступ закрыт (демо истекло / оплаченный период кончился за грейсом) → виджет
  // ПОЛНОСТЬЮ исчезает с сайта клиента (не заглушка). Пустой рендер + host display:none
  // в index.ts. Возобновляется после оплаты (следующая загрузка страницы).
  if (config.active === false) return '';

  const ranges = {
    price: config.ranges?.price ?? DEFAULT_RANGES.price,
    dp: config.ranges?.downPaymentPct ?? DEFAULT_RANGES.downPaymentPct,
    term: config.ranges?.term ?? DEFAULT_RANGES.term,
  };

  const ui = {
    titleLine1: config.ui?.titleLine1 ?? 'Подбор',
    titleLine2: config.ui?.titleLine2 ?? 'ипотеки',
    cta: config.ui?.ctaLabel ?? 'Создать заявку',
    disclaimer:
      config.ui?.disclaimer ??
      'Это предварительные предложения от банков, чтобы отправить в них заявку, нужно будет заполнить анкету на следующем шаге.',
  };

  const chips = config.programs
    .map((p) => {
      const isActive = p.id === config.defaultProgramId;
      return `<button class="wl-chip${isActive ? ' active' : ''}" data-program="${escapeAttr(p.id)}" type="button" aria-pressed="${isActive}">${escapeHtml(p.name)}</button>`;
    })
    .join('');

  // Подпись об актуальности: только когда ставки пришли из рыночного снапшота.
  // Ручные ставки датировать нечем — клиент правит их сам и когда захочет.
  const ratesUpdatedLine =
    config.ratesInfo && config.ratesInfo.source !== 'manual' && config.ratesInfo.updatedAt
      ? `Ставки банков${config.ratesInfo.city ? ` в г. ${config.ratesInfo.city}` : ''} на ${formatRuDate(config.ratesInfo.updatedAt)}`
      : '';

  const showPsk = config.ui?.showPsk !== false; // дефолт — true
  const showBreakdown = config.ui?.showBreakdown !== false; // дефолт — true (клиент может скрыть «переплату»)
  // Кликабельность hero только при доступной лид-форме (тариф business/pro).
  // Строки списка «Ещё N» кликабельны ВСЕГДА и лид НЕ открывают — они раскрывают
  // условия банка ПРЯМО НА СВОЁМ МЕСТЕ в списке (bindOfferExpand в index.ts).
  // До 20.08.2026 и hero, и строки вели в лид-форму: покупатель жал «Сбербанк»,
  // получал форму захвата контактов и не видел ни ставки, ни ПСК.
  const heroInteractive = leadForm
    ? `tabindex="0" role="button" aria-label="Лучшее предложение — создать заявку"`
    : `aria-label="Лучшее предложение"`;

  return /* html */ `
<div class="wl-mortgage-widget" role="region" aria-label="Подбор ипотеки">
  <div class="wl-widget-inner">

    <div class="wl-col-left">
      <h2 class="wl-section-title">${escapeHtml(ui.titleLine1)}<br>${escapeHtml(ui.titleLine2)}</h2>

      <div class="wl-chips-header" role="group" aria-label="Ипотечная программа">
        ${chips}
      </div>

      ${config.family ? renderFamilyBlock(normalizeChildren(config.family.defaultChildren)) : ''}

      <form data-form="calc" novalidate>
        <fieldset style="border:none;padding:0;margin:0;">
          <legend class="sr-only">Параметры ипотеки</legend>

          <div class="wl-sliders">

            <div class="wl-slider-item">
              <label class="wl-slider-label" for="wl-slider-price">Стоимость недвижимости</label>
              <div class="wl-slider-value-row">
                <span class="wl-slider-number" data-out="price" aria-live="polite">${formatRubInitial(ranges.price.default)} ₽</span>
              </div>
              <div class="wl-range-wrapper" aria-hidden="true">
                <div class="wl-range-track">
                  <div class="wl-range-fill" data-fill="price"></div>
                </div>
                <input
                  type="range"
                  id="wl-slider-price"
                  data-input="price"
                  min="${ranges.price.min}"
                  max="${ranges.price.max}"
                  step="${ranges.price.step}"
                  value="${ranges.price.default}"
                  aria-label="Стоимость недвижимости"
                >
              </div>
              <div class="wl-range-limits">
                <span>${shortMln(ranges.price.min)}</span>
                <span>${shortMln(ranges.price.max)}</span>
              </div>
            </div>

            <div class="wl-slider-item">
              <label class="wl-slider-label" for="wl-slider-dp">Первоначальный взнос</label>
              <div class="wl-slider-value-row">
                <span class="wl-slider-number" data-out="dp" aria-live="polite">${formatRubInitial(Math.round((ranges.price.default * ranges.dp.default) / 100))} ₽</span>
                <span class="wl-slider-pct" data-out="dp-pct">${formatPct(ranges.dp.default)}</span>
              </div>
              <div class="wl-range-wrapper" aria-hidden="true">
                <div class="wl-range-track">
                  <div class="wl-range-fill" data-fill="dp"></div>
                </div>
                <input
                  type="range"
                  id="wl-slider-dp"
                  data-input="dp"
                  min="${ranges.dp.min}"
                  max="${ranges.dp.max}"
                  step="${ranges.dp.step}"
                  value="${ranges.dp.default}"
                  aria-label="Первоначальный взнос в процентах"
                >
              </div>
              <div class="wl-range-limits">
                <span>${formatPct(ranges.dp.min)}</span>
                <span>${formatPct(ranges.dp.max)}</span>
              </div>
            </div>

            <div class="wl-slider-item">
              <label class="wl-slider-label" for="wl-slider-term">Срок кредита</label>
              <div class="wl-slider-value-row">
                <span class="wl-slider-number" data-out="term" aria-live="polite">${ranges.term.default} лет</span>
              </div>
              <div class="wl-range-wrapper" aria-hidden="true">
                <div class="wl-range-track">
                  <div class="wl-range-fill" data-fill="term"></div>
                </div>
                <input
                  type="range"
                  id="wl-slider-term"
                  data-input="term"
                  min="${ranges.term.min}"
                  max="${ranges.term.max}"
                  step="${ranges.term.step}"
                  value="${ranges.term.default}"
                  aria-label="Срок кредита в годах"
                >
              </div>
              <div class="wl-range-limits">
                <span>${ranges.term.min} лет</span>
                <span>${ranges.term.max} лет</span>
              </div>
            </div>

          </div>
        </fieldset>
      </form>

      ${
        leadForm
          ? `<button class="wl-cta-btn" data-action="open-modal" type="button" aria-haspopup="dialog">
        <span>${escapeHtml(ui.cta)}</span>
        <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 9h12M9 3l6 6-6 6"/>
        </svg>
      </button>`
          : ''
      }

      <p class="wl-disclaimer">${escapeHtml(
        leadForm
          ? ui.disclaimer
          : 'Предварительный расчёт по вашим параметрам. Не является публичной офертой.',
      )}</p>
    </div>

    <div class="wl-col-right" aria-live="polite" aria-label="Предложения банков">
      <div class="wl-results-head">
        <span class="wl-results-title">Лучшие предложения</span>
        <span class="wl-results-meta">кредит <b data-out="loan">—</b></span>
      </div>
      ${ratesUpdatedLine ? `<p class="wl-rates-updated">${escapeHtml(ratesUpdatedLine)}</p>` : ''}

      <article class="wl-hero" data-hero ${heroInteractive}>
        <div class="wl-hero-top">
          <div class="wl-hero-bankrow">
            <div class="wl-hero-logo" data-hero-logo aria-hidden="true"></div>
            <div>
              <div class="wl-badge-best" data-hero-best hidden>★ Лучшее</div>
              <div class="wl-badge-special" data-hero-special hidden>—</div>
              <div class="wl-hero-bankname" data-hero-name>—</div>
              <div class="wl-hero-bankrate">ставка <span class="wl-r" data-hero-rate>—</span> · <span data-hero-prog>—</span></div>
            </div>
          </div>
          <div class="wl-hero-save" data-hero-savewrap hidden>
            <span class="wl-save-badge">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 1.5v9M2.2 6.6 6 10.5l3.8-3.9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <span data-hero-save>—</span>
            </span>
          </div>
        </div>

        <div class="wl-hero-price">
          <span class="wl-price-label">Ежемесячный платёж</span>
          <div class="wl-price-big"><span data-hero-pay>—</span> <span class="wl-cur">₽</span><span class="wl-per">/мес</span></div>
          <p class="wl-hero-note wl-hero-note--after" data-hero-after hidden></p>
          <p class="wl-hero-note" data-hero-combo hidden></p>
        </div>

        ${showPsk ? `<div class="wl-hero-psk" data-hero-psk>—</div>` : ''}

        ${showBreakdown ? `<div class="wl-breakdown">
          <span class="wl-bkd-label">Из чего складывается общая выплата</span>
          <div class="wl-bar" role="img" aria-label="Соотношение тела кредита и процентов">
            <div class="wl-bar-seg wl-bar-body" data-seg-body style="flex-basis:50%"></div>
            <div class="wl-bar-seg wl-bar-int"  data-seg-int  style="flex-basis:50%"></div>
          </div>
          <div class="wl-legend">
            <div class="wl-leg">
              <span class="wl-leg-dot wl-leg-dot--body"></span>
              <span class="wl-leg-txt">
                <span class="wl-leg-name">Тело кредита</span>
                <span class="wl-leg-sum" data-sum-body>—</span>
              </span>
            </div>
            <div class="wl-leg">
              <span class="wl-leg-dot wl-leg-dot--int"></span>
              <span class="wl-leg-txt">
                <span class="wl-leg-name">Проценты за срок</span>
                <span class="wl-leg-sum" data-sum-int>—</span>
              </span>
            </div>
          </div>
        </div>` : ''}
      </article>

      <div class="wl-more" data-more data-open="true" hidden>
        <button type="button" class="wl-more-toggle" data-action="toggle-more" aria-expanded="true" aria-controls="wl-more-list-${escapeAttr(config.tenantId)}">
          <span data-more-label>Свернуть предложения</span>
          <svg class="wl-more-chev" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <div class="wl-more-list" id="wl-more-list-${escapeAttr(config.tenantId)}">
          <div class="wl-more-inner" data-more-inner></div>
        </div>
      </div>

      <p class="wl-dropped" data-dropped hidden></p>

      <div class="wl-no-offers" data-no-offers hidden>
        <h3 class="wl-no-offers-title" data-no-offers-title>${escapeHtml(NO_OFFERS_TITLE)}</h3>
        <p class="wl-no-offers-text" data-no-offers-text>${escapeHtml(NO_OFFERS_DEFAULT_TEXT)}</p>
        <div class="wl-no-offers-actions">
          <button type="button" class="wl-no-btn wl-no-btn--primary" data-action="raise-down" hidden></button>
          <button type="button" class="wl-no-btn" data-action="other-program" hidden>${escapeHtml(OTHER_PROGRAM_LABEL)}</button>
        </div>
      </div>
    </div>

  </div>
  ${renderWlBadge(branding, tenantSlug || config.tenantId)}
</div>

${leadForm ? renderModal(config) : ''}
`;
}

/** Текст пустой выдачи по умолчанию; для «Семейной» его заменяет причина (index.ts). */
export const NO_OFFERS_DEFAULT_TEXT =
  'По выбранной программе банки не предоставляют условия. Выберите другую программу.';

/**
 * Блок «Детей в семье» под чипами программ — виден только при выбранной
 * «Семейной» (index.ts снимает `hidden`). Число детей — группа радио-кнопок
 * (role=radiogroup): одна выбрана, стрелки двигают выбор, в Tab-порядке только
 * выбранная. На каждой кнопке — число детей и мелко ставка яруса; ставки и
 * пояснение под кнопками заполняет recalc.
 */
function renderFamilyBlock(children: number): string {
  const buttons = CHILDREN_LABELS.map((label, i) => {
    const n = i + 1;
    const on = n === children;
    return `<button type="button" class="wl-seg-btn" role="radio" aria-checked="${on}" tabindex="${on ? 0 : -1}" data-children="${n}" aria-label="${escapeAttr(label)}"><span class="wl-seg-n">${n === 5 ? '5+' : n}</span><span class="wl-seg-r" data-seg-rate></span></button>`;
  }).join('');
  return /* html */ `<div class="wl-family" data-family hidden>
        <div class="wl-family-field">
          <span class="wl-slider-label" id="wl-family-children-label">Детей в семье</span>
          <div class="wl-seg" role="radiogroup" aria-labelledby="wl-family-children-label" data-family-seg>${buttons}</div>
        </div>
        <label class="wl-family-check">
          <input type="checkbox" data-family-disabled>
          <span>Есть ребёнок с инвалидностью</span>
        </label>
        <p class="wl-family-hint" data-family-hint aria-live="polite">
          <span class="wl-family-hint-main" data-family-hint-main></span>
          <span class="wl-family-hint-tip" data-family-hint-tip hidden></span>
          <span class="wl-family-hint-note" data-family-hint-note></span>
        </p>
      </div>`;
}

/** Лид-модалка — рендерится только на тарифах с лид-формой (business/pro). */
function renderModal(config: WidgetConfig): string {
  return /* html */ `
<div class="wl-modal-overlay" data-modal="overlay" role="dialog" aria-modal="true" aria-labelledby="wl-modal-title-${escapeAttr(config.tenantId)}">
  <div class="wl-modal">
    <button class="wl-modal-close" data-action="close-modal" aria-label="Закрыть" type="button">&times;</button>

    <div class="wl-modal-form" data-modal="form">
      <h2 class="wl-modal-title" id="wl-modal-title-${escapeAttr(config.tenantId)}">Получить подборку<br>от банков</h2>
      <p class="wl-modal-sub" data-modal="bank-info">Отправим предложения банков по вашим параметрам.</p>

      <div class="wl-form-group">
        <label class="wl-form-label" for="wl-inp-name-${escapeAttr(config.tenantId)}">Имя <span aria-hidden="true" style="color:#EF3124;">*</span></label>
        <input class="wl-form-input" type="text" id="wl-inp-name-${escapeAttr(config.tenantId)}" data-input="name" name="name" placeholder="Иван Иванов" autocomplete="name" required>
      </div>

      <div class="wl-form-group">
        <label class="wl-form-label" for="wl-inp-phone-${escapeAttr(config.tenantId)}">Телефон <span aria-hidden="true" style="color:#EF3124;">*</span></label>
        <input class="wl-form-input" type="tel" id="wl-inp-phone-${escapeAttr(config.tenantId)}" data-input="phone" name="phone" placeholder="+7 (900) 000-00-00" autocomplete="tel" inputmode="tel" required>
      </div>

      <label class="wl-consent-row">
        <input type="checkbox" data-input="consent" name="consent" required>
        <span class="wl-consent-text" data-consent-text>Даю согласие на обработку персональных данных и ознакомлен с <a href="#" data-action="open-policy">политикой обработки персональных данных</a></span>
      </label>

      <div class="wl-form-error" data-modal-error hidden role="alert"></div>

      <button class="wl-modal-submit" type="button" data-action="submit-lead">Получить предложения</button>
    </div>

    <div class="wl-modal-success" data-modal="success">
      <div class="wl-success-icon">
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
          <polyline points="5,14 11,20 23,8" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="color: var(--w-brand)"/>
        </svg>
      </div>
      <h3 class="wl-success-title">Заявка отправлена</h3>
      <p class="wl-success-text">Мы свяжемся с вами в ближайшее время и пришлём персональные предложения от банков.</p>
    </div>
  </div>
</div>
`;
}

/**
 * Внутренность плашки логотипа банка. Для банков из каталога @wl/bank-catalog
 * (совпадение по code == bank.id) — брендовый SVG, заполняющий плашку целиком
 * (свой фон-квадрат, скругление даёт контейнер через overflow:hidden). Для банков
 * вне каталога (кастомные tenant-банки) — буква на цветном фоне: цвет плашки задаёт
 * вызывающий код через inline background из safeColor(bank.logoColor). Используется
 * в hero и offer-строках (заполняются из index.ts в recalc).
 */
export function bankLogoInner(bank: { id: string; logoLetter: string }): string {
  const brand = getBankLogoSvg(bank.id);
  if (brand) return brand;
  return escapeHtml(bank.logoLetter);
}

/** Чевроны лок-апа ‹ › (indigo #5B5FF5) — каноничная геометрия brand-lockup. */
const WL_CHEV_L = /* html */ `<svg class="wl-chev" viewBox="0 0 13 30" fill="none" aria-hidden="true"><path d="M10 4 L4 15 L10 26" stroke="#5B5FF5" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const WL_CHEV_R = /* html */ `<svg class="wl-chev" viewBox="0 0 13 30" fill="none" aria-hidden="true"><path d="M3 4 L9 15 L3 26" stroke="#5B5FF5" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/**
 * Бейдж продукта widgy внизу виджета. Текст «работает на ‹widgy›» — на обоих
 * платных тарифах, с анимацией: точка-курсор пульсирует (канонический brand
 * motion `wlw-pulse` из tailwind.config — «инструмент работает», 1:1 с баннером/
 * лок-апом). Отличается только оправа:
 *   full (тариф старт)   — сплошная видимая полоса внизу виджета (.wl-badge-bar);
 *   compact (тариф бизнес) — нативный элемент без полосы (.wl-badge-native);
 *   none (pro)           — скрыт.
 * Лок-ап: indigo-чевроны + wordmark Space Grotesk + точка-курсор. Индиго — всегда
 * продуктовый, не тема клиента. Ссылка на продукт widgy.site.
 */
/**
 * Адрес бейджа с метками. Бейдж висит на сайтах клиентов и приводит нам трафик,
 * но до 04.08.2026 вёл на голый https://widgy.site — в Метрике эти переходы
 * сливались с прямыми заходами, и было не видно ни сколько их, ни от какого
 * клиента. Контракт меток (тот же в apps/widget-chat/src/template.ts):
 *
 *   utm_source   widgy_widget   — вся «бейджевая» витрина одной группой
 *   utm_medium   badge
 *   utm_campaign <слаг тенанта> — какой клиент приводит трафик
 *   utm_content  <вид виджета>  — калькулятор, чат или витрина
 *   utm_term     <хост сайта>   — где именно висит, если удалось определить
 *
 * Целей в Метрике под это заводить НЕ надо: переход с бейджа — источник, а не
 * действие, он читается в «Источники → Метки UTM». Что человек сделает дальше,
 * меряют уже заведённые цели лендинга (demo_click, tariff_*, manager_click).
 *
 * Метки лендинг подхватывает сам и доносит до CRM: apps/landing/app/marketing.ts
 * кладёт их в ссылки на кабинет, apps/admin/lib/marketing.ts шлёт с заявкой.
 */
export function widgyBadgeHref(kind: string, tenantSlug: string): string {
  const u = new URL('https://widgy.site/');
  u.searchParams.set('utm_source', 'widgy_widget');
  u.searchParams.set('utm_medium', 'badge');
  if (tenantSlug) u.searchParams.set('utm_campaign', tenantSlug);
  u.searchParams.set('utm_content', kind);
  try {
    // Хост страницы, на которой стоит виджет. Внутри нашей iframe-обёртки
    // /embed/* hostname — наш собственный, он бесполезен: там берём referrer.
    // Проверяем ПУТЬ, а не список доменов: обёртка /embed/* живёт минимум на
    // двух наших хостах, и по домену метка уезжала в widgets.wlabel.pro вместо
    // сайта клиента (поймано 11.08.2026).
    const h = location.hostname;
    const inOurFrame = location.pathname.startsWith('/embed/') || /(^|\.)widgy\.site$/.test(h);
    const host = inOurFrame
      ? (document.referrer ? new URL(document.referrer).hostname : '')
      : h;
    if (host) u.searchParams.set('utm_term', host);
  } catch {
    /* нестандартное окружение — метка хоста не критична */
  }
  return u.toString();
}

function renderWlBadge(branding: 'full' | 'compact' | 'none', tenantSlug: string): string {
  if (branding === 'none') return '';
  const wrapCls = branding === 'full' ? 'wl-badge-bar' : 'wl-badge-native';
  const href = escapeAttr(widgyBadgeHref('mortgage', tenantSlug));
  return /* html */ `
<div class="${wrapCls}"><a class="wl-badge" href="${href}" target="_blank" rel="noopener" aria-label="Работает на widgy"><span class="wl-badge-pre">работает на</span><span class="wl-lockup" aria-hidden="true">${WL_CHEV_L}<span class="wl-lk-clip"><span class="wl-wm">widgy</span></span><span class="wl-lk-dot"></span>${WL_CHEV_R}</span></a></div>`;
}

/* ── helpers ── */

/**
 * ISO-дата → «4 августа 2026». Через Intl не делаем: виджет живёт на чужом
 * сайте, где локаль браузера может быть любой, а подпись обязана читаться
 * по-русски независимо от посетителя.
 */
export function formatRuDate(iso: string): string {
  const MONTHS = [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря',
  ];
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()] ?? ''} ${d.getFullYear()}`;
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function escapeAttr(s: string): string {
  return s.replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/**
 * Только валидный hex-цвет, иначе fallback. escapeAttr не экранирует `;}()` и пробелы,
 * поэтому tenant-controlled цвет вида `red;background-image:url(...)` мог инъектить CSS
 * в inline-style. Здесь жёстко ограничиваем формат.
 */
export function safeColor(s: string | undefined, fallback: string): string {
  return typeof s === 'string' &&
    /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(s.trim())
    ? s.trim()
    : fallback;
}

function formatRubInitial(n: number): string {
  return Math.round(n).toLocaleString('ru-RU');
}

function shortMln(n: number): string {
  // «4,5 млн» — запятая, как и везде в виджете. Целое печатается без «,0».
  if (n >= 1_000_000) return `${formatNum(n / 1_000_000, 1)} млн`;
  return formatRubInitial(n);
}
